import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  getTableActionButtonClass,
  getTableActionsConfig,
} from '../../config/tableActionButtons';
import { useTableUiTheme } from '../../contexts/TableUiThemeContext';
import {
  assigneeHistoryStepBadgeClassName,
  attachmentSourceBadgeClassName,
  categoryBadgeClassName,
  departmentBadgeClassName,
  messageCountChipClassName,
  ticketingDropZoneActiveClassName,
  ticketingDropZoneIdleClassName,
  ticketingExpandedPayloadBgClassName,
  ticketingInfoPanelClassName,
  ticketingInfoPanelTitleClassName,
  ticketingLinkClassName,
  ticketingStatusBadgeClassName,
} from '../../config/tableBadgeStyles';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { EyeIcon, PencilIcon, TrashIcon, PaperClipIcon, InfoIcon } from '../../components/ui/Icons';
import {
  Card,
  CardContent,
  Button,
  Label,
  Select,
  SelectItem,
  Input,
  Textarea,
  DateInput,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ConfirmationDialog,
} from '../../components/ui';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../components/ui/Table';
import {
  fetchTicketingDepartments,
  fetchTicketingRequests,
  updateTicketingRequest,
  deleteTicketingRequest,
  fetchTicketingRequest,
  fetchTicketingRequestMessages,
  fetchTicketingAssignableUsers,
  uploadTicketingRequestAttachment,
  downloadTicketingAttachmentObjectUrl,
} from './ticketingApi';
import { TICKETING_DATA_CHANGED_EVENT } from './ticketingStorage';
import { TicketingPayloadViewer } from './components/TicketingPayloadViewer';
import { TicketingOriginalDescriptionFooter } from './components/TicketingOriginalDescriptionFooter';
import { TicketingConversationModal } from './components/TicketingConversationModal';
import { getTicketingPayloadSummary } from './ticketingPayloadSummary';
import { normalizeFileName } from '../../components/ui/utils/fileNameEncoding';
import { mergeTicketAttachmentsForDisplay } from './ticketingAttachmentsMerge';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN,IN_PROGRESS', label: 'Open & In progress' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const EDIT_STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function formatShortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '—';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function formatOptionalShortDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '';
  }
}

function toDateValue(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function lastMessageAuthorName(msg) {
  const a = msg?.author;
  if (!a) return '—';
  return a.userDisplayName || a.userEmail || (a.id != null ? `User #${a.id}` : '—');
}

function auditActionLabel(action) {
  const m = {
    STATUS: 'Status',
    ASSIGNEE: 'Assignee',
    NOTES: 'Notes',
    DUE_AT: 'Due date',
  };
  return m[action] || action;
}

const DEPARTMENT_PALETTE_COUNT = 10;

function hashKeyToDepartmentPaletteIndex(key) {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h << 5) - h + key.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % DEPARTMENT_PALETTE_COUNT;
}

function departmentBadgeClasses(departmentId, departmentName) {
  const idNum = departmentId != null ? Number(departmentId) : NaN;
  const key =
    Number.isFinite(idNum) && idNum > 0
      ? `id:${idNum}`
      : typeof departmentName === 'string' && departmentName.trim()
        ? `name:${departmentName.trim().toLowerCase()}`
        : '—';
  return departmentBadgeClassName(hashKeyToDepartmentPaletteIndex(key));
}

function ticketSubjectText(ticket) {
  const topLevel = typeof ticket?.subject === 'string' ? ticket.subject.trim() : '';
  if (topLevel) return topLevel;
  const payloadSubject =
    ticket?.payload && typeof ticket.payload === 'object' && !Array.isArray(ticket.payload)
      ? ticket.payload.subject
      : '';
  return typeof payloadSubject === 'string' && payloadSubject.trim()
    ? payloadSubject.trim()
    : '';
}

function assigneeDisplayText(ticket) {
  const assignee = ticket?.assignedToUser;
  if (!assignee) return '';
  return (
    assignee.userDisplayName ||
    assignee.userEmail ||
    (ticket?.assignedToUserId != null ? `User #${ticket.assignedToUserId}` : '')
  );
}

function parseAssigneeTransferHistory(history) {
  const raw = typeof history === 'string' ? history.trim() : '';
  if (!raw) return [];
  const parts = raw
    .split('->')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.map((part) => {
    const splitAt = part.lastIndexOf(' - ');
    if (splitAt > 0) {
      return {
        user: part.slice(0, splitAt).trim(),
        date: part.slice(splitAt + 3).trim(),
      };
    }
    return { user: part, date: '' };
  });
}

const TicketingItQueuePage = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { themeId: tableUiThemeId } = useTableUiTheme();
  void tableUiThemeId;
  const dash = useDashboardUi(theme);
  const pendingEditTicketIdRef = useRef(null);
  const tableActionDensity = 'normal';
  const tableActionSpacing = getTableActionsConfig('spacing');
  const showActionTooltips = getTableActionsConfig('showTooltips');
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('OPEN,IN_PROGRESS');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [attachmentsTicket, setAttachmentsTicket] = useState(null);
  const [attachmentsTicketMessages, setAttachmentsTicketMessages] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [editStatus, setEditStatus] = useState('OPEN');
  const [editAssignedToUserId, setEditAssignedToUserId] = useState('');
  const [editDueAt, setEditDueAt] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedPayloadId, setExpandedPayloadId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketPendingDelete, setTicketPendingDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [conversationTicket, setConversationTicket] = useState(null);
  const [search, setSearch] = useState('');
  const [createdDate, setCreatedDate] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [modalUploading, setModalUploading] = useState(false);
  const [isDragOverAttachments, setIsDragOverAttachments] = useState(false);
  const modalFileRef = useRef(null);

  const loadDepartments = useCallback(async () => {
    try {
      const list = await fetchTicketingDepartments();
      setDepartments(list || []);
    } catch {
      setDepartments([]);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchTicketingRequests({
        departmentId: departmentId || undefined,
        status: status || undefined,
        search: search.trim() || undefined,
      });
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Failed to load tickets');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId, status, search]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    const loadAssignableUsers = async () => {
      try {
        const users = await fetchTicketingAssignableUsers();
        setAssignableUsers(Array.isArray(users) ? users : []);
      } catch {
        setAssignableUsers([]);
      }
    };
    loadAssignableUsers();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    const onData = () => loadTickets();
    window.addEventListener(TICKETING_DATA_CHANGED_EVENT, onData);
    return () => window.removeEventListener(TICKETING_DATA_CHANGED_EVENT, onData);
  }, [loadTickets]);

  const openModal = async (ticketRow) => {
    setModalOpen(true);
    setSelected(ticketRow);
    setSelectedMessages([]);
    setEditStatus(ticketRow.status || 'OPEN');
    setEditAssignedToUserId(
      ticketRow.assignedToUserId != null ? String(ticketRow.assignedToUserId) : '',
    );
    setEditDueAt(toDateValue(ticketRow.dueAt));
    setEditNotes(ticketRow.notes || '');
    try {
      const full = await fetchTicketingRequest(ticketRow.id);
      setSelected(full);
      setEditStatus(full.status || 'OPEN');
      setEditAssignedToUserId(
        full.assignedToUserId != null ? String(full.assignedToUserId) : '',
      );
      setEditDueAt(toDateValue(full.dueAt));
      setEditNotes(full.notes || '');
      let msgs = [];
      try {
        msgs = await fetchTicketingRequestMessages(ticketRow.id);
      } catch {
        /* same combined list as requester view when messages endpoint fails */
      }
      setSelectedMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      /* keep row snapshot */
    }
  };

  useEffect(() => {
    const editTicketId = location.state?.editTicketId;
    if (editTicketId == null || editTicketId === '') return;
    if (loading) return;
    if (pendingEditTicketIdRef.current === editTicketId) return;
    pendingEditTicketIdRef.current = editTicketId;

    const openFromDashboard = async () => {
      const row = rows.find((t) => String(t.id) === String(editTicketId));
      try {
        if (row) {
          await openModal(row);
        } else {
          const full = await fetchTicketingRequest(editTicketId);
          await openModal(full);
        }
      } catch {
        /* ticket missing or load failed */
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
        pendingEditTicketIdRef.current = null;
      }
    };
    void openFromDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when navigated from dashboard with editTicketId
  }, [loading, rows, location.state?.editTicketId]);

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setSelected(null);
    setSelectedMessages([]);
  };

  const openAttachmentsModal = async (ticketRow) => {
    if (!ticketRow?.id) return;
    setAttachmentsModalOpen(true);
    setAttachmentsLoading(true);
    setAttachmentsTicket(ticketRow);
    setAttachmentsTicketMessages([]);
    try {
      const full = await fetchTicketingRequest(ticketRow.id);
      setAttachmentsTicket(full);
      let msgs = [];
      try {
        msgs = await fetchTicketingRequestMessages(ticketRow.id);
      } catch {
        /* ignore */
      }
      setAttachmentsTicketMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      // keep row snapshot
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const closeAttachmentsModal = () => {
    if (attachmentsLoading) return;
    setAttachmentsModalOpen(false);
    setAttachmentsTicket(null);
    setAttachmentsTicketMessages([]);
  };

  const uploadAttachmentForSelected = useCallback(
    async (file) => {
      if (!file || !selected?.id) return;
      setModalUploading(true);
      try {
        await uploadTicketingRequestAttachment(selected.id, file, null);
        const full = await fetchTicketingRequest(selected.id);
        setSelected(full);
        let msgs = [];
        try {
          msgs = await fetchTicketingRequestMessages(selected.id);
        } catch {
          /* ignore */
        }
        setSelectedMessages(Array.isArray(msgs) ? msgs : []);
        await loadTickets();
      } catch (err) {
        alert(err?.message || 'Upload failed');
      } finally {
        setModalUploading(false);
      }
    },
    [selected?.id, loadTickets],
  );

  const handleSave = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await updateTicketingRequest(selected.id, {
        status: editStatus,
        assignedToUserId: editAssignedToUserId ? Number(editAssignedToUserId) : null,
        dueAt: editDueAt || null,
        notes: editNotes.trim() || '',
      });
      await loadTickets();
      closeModal();
    } catch (e) {
      alert(e?.message || 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (ticket) => {
    if (!ticket?.id) return;
    setTicketPendingDelete(ticket);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = (force = false) => {
    if (!force && deleteSubmitting) return;
    setDeleteDialogOpen(false);
    setTicketPendingDelete(null);
  };

  const confirmDeleteTicket = async () => {
    const ticket = ticketPendingDelete;
    if (!ticket?.id) return;
    setDeleteSubmitting(true);
    try {
      await deleteTicketingRequest(ticket.id);
      if (selected?.id === ticket.id) {
        setModalOpen(false);
        setSelected(null);
        setSelectedMessages([]);
      }
      if (conversationTicket?.id === ticket.id) {
        setConversationTicket(null);
      }
      closeDeleteDialog(true);
      await loadTickets();
    } catch (e) {
      alert(e?.message || 'Failed to delete ticket');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    return sortedRows.filter((ticket) => {
      if (createdDate) {
        const ticketCreatedDate = toDateValue(ticket.createdAt);
        if (ticketCreatedDate !== createdDate) return false;
      }

      if (assigneeFilter === 'na') {
        return ticket.assignedToUserId == null;
      }

      if (assigneeFilter) {
        return String(ticket.assignedToUserId || '') === assigneeFilter;
      }

      return true;
    });
  }, [sortedRows, createdDate, assigneeFilter]);

  const assigneeHistorySteps = useMemo(
    () => parseAssigneeTransferHistory(selected?.assigneeTransferHistory),
    [selected?.assigneeTransferHistory],
  );

  const mergedEditAttachments = useMemo(
    () => mergeTicketAttachmentsForDisplay(selected, selectedMessages),
    [selected, selectedMessages],
  );

  const mergedAttachmentsModalList = useMemo(
    () => mergeTicketAttachmentsForDisplay(attachmentsTicket, attachmentsTicketMessages),
    [attachmentsTicket, attachmentsTicketMessages],
  );

  return (
    <div className={cn('min-h-full', dash.pageBg())}>
      <div className="w-full max-w-none">
        <Card className={cn('w-full', dash.widgetShell('p-0'))}>
          <CardContent className="space-y-4 p-3 pt-3">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-end">
              <div className="w-full sm:w-auto sm:min-w-[220px]">
                <Label htmlFor="it-queue-dept" className="mb-1.5 block">
                  Department
                </Label>
                <Select
                  id="it-queue-dept"
                  name="departmentId"
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  showClearButton={false}
                  className="w-full"
                >
                  <SelectItem value="">All departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className="w-full sm:w-auto sm:min-w-[200px]">
                <Label htmlFor="it-queue-status" className="mb-1.5 block">
                  Status
                </Label>
                <Select
                  id="it-queue-status"
                  name="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  showClearButton={false}
                  className="w-full"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md">
                <Label htmlFor="it-queue-search" className="mb-1.5 block">
                  Search
                </Label>
                <Input
                  id="it-queue-search"
                  name="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ticket #, requester, payload text…"
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-auto sm:min-w-[180px]">
                <Label htmlFor="it-queue-created-date" className="mb-1.5 block">
                  Date Created
                </Label>
                <DateInput
                  id="it-queue-created-date"
                  name="createdDate"
                  value={createdDate}
                  onChange={(e) => setCreatedDate(e.target.value)}
                  showClearButton={false}
                  showFormatHint={false}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-auto sm:min-w-[220px]">
                <Label htmlFor="it-queue-assignee-filter" className="mb-1.5 block">
                  Assignee
                </Label>
                <Select
                  id="it-queue-assignee-filter"
                  name="assigneeFilter"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  showClearButton={false}
                  className="w-full"
                >
                  <SelectItem value="">All assignees</SelectItem>
                  <SelectItem value="na">N/A</SelectItem>
                  {assignableUsers.map((u) => {
                    const label =
                      u.userDisplayName || u.userEmail || (u.id != null ? `User #${u.id}` : 'Unknown');
                    return (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </Select>
              </div>
              <Button type="button" variant="secondary" onClick={() => loadTickets()} disabled={loading}>
                Refresh
              </Button>
            </div>

            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : null}

            {loading ? (
              <p className={cn('text-sm py-8 text-center', theme.text.muted)}>Loading tickets…</p>
            ) : filteredRows.length === 0 ? (
              <p className={cn('text-sm py-8 text-center', theme.text.muted)}>No tickets match.</p>
            ) : (
              <Table className="min-w-[1220px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                    <TableHead>Ticket date</TableHead>
                    <TableHead className="min-w-[180px] max-w-[260px]">Subject</TableHead>
                    <TableHead className="min-w-[140px] max-w-[200px] w-[170px]">
                      <span className="block leading-tight">Department</span>
                      <span className={cn('block text-[10px] font-normal leading-tight', theme.text.muted)}>
                        Category
                      </span>
                    </TableHead>
                    <TableHead className="min-w-[320px] max-w-[520px] w-[400px]">Request</TableHead>
                    <TableHead className="min-w-[132px] max-w-[200px]">
                      <span className="block leading-tight">Assignee</span>
                      <span className={cn('block text-[10px] font-normal leading-tight', theme.text.muted)}>
                        Due date
                      </span>
                    </TableHead>
                    <TableHead className="w-[110px]">Status</TableHead>
                    <TableHead>Requested by</TableHead>
                    <TableHead className="min-w-[200px] max-w-[280px] w-[240px]">
                      <span className="block leading-tight">Latest message</span>
                      <span className={cn('block text-[10px] font-normal leading-tight', theme.text.muted)}>
                        Preview · open for full thread
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((t) => {
                    const dept =
                      typeof t.ticketingCategory?.ticketingDepartment?.name === 'string'
                        ? t.ticketingCategory.ticketingDepartment.name.trim()
                        : '';
                    const deptId = t.ticketingCategory?.ticketingDepartment?.id;
                    const cat =
                      typeof t.ticketingCategory?.name === 'string'
                        ? t.ticketingCategory.name.trim()
                        : '';
                    const req = t.ticketRequestedFromUser;
                    const who =
                      req?.userDisplayName ||
                      req?.userEmail ||
                      (t.ticketRequestedFromUserId != null
                        ? `User #${t.ticketRequestedFromUserId}`
                        : '—');
                    const { full: payloadFull, preview: payloadPreview } = getTicketingPayloadSummary(
                      t.formKey,
                      t.payload,
                    );
                    const subjectText = ticketSubjectText(t);
                    const isPayloadOpen = expandedPayloadId === t.id;
                    return (
                      <React.Fragment key={t.id}>
                        <TableRow>
                          <TableCell className="font-mono font-medium">
                            <Link
                              to={`/ticketing/ticket/${t.id}`}
                              className={ticketingLinkClassName(theme)}
                            >
                              #{t.id}
                            </Link>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div
                              className={cn(
                                'flex items-center justify-center whitespace-nowrap',
                                tableActionSpacing,
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => setConversationTicket(t)}
                                className={getTableActionButtonClass('view', tableActionDensity, theme)}
                                title={showActionTooltips ? 'Open full message history' : undefined}
                                aria-label={`Open comments for ticket ${t.id}`}
                              >
                                <EyeIcon
                                  className={cn('advanced-action-icon', `density-${tableActionDensity}`)}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => openModal(t)}
                                className={getTableActionButtonClass('edit', tableActionDensity, theme)}
                                title={showActionTooltips ? 'View / update ticket' : undefined}
                                aria-label={`View or update ticket ${t.id}`}
                              >
                                <PencilIcon
                                  className={cn('advanced-action-icon', `density-${tableActionDensity}`)}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteDialog(t)}
                                className={getTableActionButtonClass('delete', tableActionDensity, theme)}
                                title={showActionTooltips ? 'Delete ticket' : undefined}
                                aria-label={`Delete ticket ${t.id}`}
                              >
                                <TrashIcon
                                  className={cn('advanced-action-icon', `density-${tableActionDensity}`)}
                                />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell>{formatShortDate(t.ticketDate)}</TableCell>
                          <TableCell className="max-w-[260px] truncate" title={subjectText || undefined}>
                            {subjectText}
                          </TableCell>
                          <TableCell className="min-w-[140px] max-w-[200px] w-[170px] align-top">
                            {dept ? (
                              <span
                                className={cn(
                                  'inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                                  departmentBadgeClasses(deptId, dept),
                                )}
                                title={dept}
                              >
                                <span className="truncate">{dept}</span>
                              </span>
                            ) : null}
                            {cat ? (
                              <span
                                className={cn(
                                  'inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                                  categoryBadgeClassName(theme),
                                  dept ? 'mt-1.5' : '',
                                )}
                                title={cat}
                              >
                                <span className="truncate">{cat}</span>
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="align-top min-w-[320px] max-w-[520px] w-[400px]">
                            <p
                              className={cn(
                                'text-xs leading-snug whitespace-pre-wrap break-words m-0 mb-2',
                                theme.text.secondary,
                              )}
                              title={payloadFull}
                            >
                              {payloadPreview}
                            </p>
                            <div className="flex items-center gap-1 -ml-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() =>
                                  setExpandedPayloadId((id) => (id === t.id ? null : t.id))
                                }
                                aria-expanded={isPayloadOpen}
                                aria-controls={`ticket-payload-${t.id}`}
                              >
                                {isPayloadOpen ? 'Hide payload' : 'Show full payload'}
                              </Button>
                              {(t.attachmentCount ?? 0) > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => openAttachmentsModal(t)}
                                  className={cn(
                                    getTableActionButtonClass('html', tableActionDensity, theme),
                                    'inline-flex items-center gap-1 shrink-0',
                                  )}
                                  title={
                                    showActionTooltips
                                      ? `Attachments (${t.attachmentCount})`
                                      : undefined
                                  }
                                  aria-label={`Open attachments for ticket ${t.id}, ${t.attachmentCount} file(s)`}
                                >
                                  <PaperClipIcon
                                    className={cn('advanced-action-icon shrink-0', `density-${tableActionDensity}`)}
                                    aria-hidden
                                  />
                                  <span className="text-xs tabular-nums leading-none">
                                    ({t.attachmentCount})
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] align-top">
                            <p
                              className="m-0 truncate text-sm"
                              title={assigneeDisplayText(t) || undefined}
                            >
                              {assigneeDisplayText(t)}
                            </p>
                            <p
                              className={cn('m-0 text-xs tabular-nums', theme.text.muted, assigneeDisplayText(t) ? 'mt-1' : '')}
                              title={t.dueAt ? formatOptionalShortDate(t.dueAt) : undefined}
                            >
                              {formatOptionalShortDate(t.dueAt)}
                            </p>
                          </TableCell>
                          <TableCell className="align-middle w-[110px]">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
                                ticketingStatusBadgeClassName(t.status || 'OPEN'),
                              )}
                            >
                              {EDIT_STATUS_OPTIONS.find((o) => o.value === (t.status || 'OPEN'))?.label || t.status || 'Open'}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            <p className="m-0 truncate" title={who}>
                              {who}
                            </p>
                            <p className={cn('m-0 mt-1 text-xs whitespace-nowrap', theme.text.muted)}>
                              {formatDateTime(t.createdAt)}
                            </p>
                          </TableCell>
                          <TableCell className="align-top min-w-[200px] max-w-[280px] w-[240px]">
                            {t.lastMessage ? (
                              <button
                                type="button"
                                onClick={() => setConversationTicket(t)}
                                className={cn(
                                  'group w-full cursor-pointer rounded-xl px-2 py-2 text-left transition-colors',
                                  'hover:bg-slate-100/95 active:bg-slate-200/80 dark:hover:bg-slate-800/70 dark:active:bg-slate-800',
                                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/45 focus-visible:ring-offset-1',
                                  'dark:focus-visible:ring-offset-slate-950',
                                )}
                                title="Open full message history"
                                aria-label={`Open message thread for ticket ${t.id}`}
                              >
                                <p
                                  className={cn(
                                    'm-0 text-xs leading-snug line-clamp-3 whitespace-pre-wrap break-words',
                                    theme.text.primary,
                                    'group-hover:text-slate-900 dark:group-hover:text-slate-50',
                                  )}
                                >
                                  {t.lastMessage.body}
                                </p>
                                <div className="mt-2 flex items-center gap-2">
                                  <p
                                    className={cn(
                                      'm-0 min-w-0 flex-1 truncate text-[10px] leading-tight',
                                      theme.text.muted,
                                    )}
                                    title={`${lastMessageAuthorName(t.lastMessage)} · ${formatDateTime(t.lastMessage.createdAt)}`}
                                  >
                                    <span className={cn('font-medium', theme.text.secondary)}>
                                      {lastMessageAuthorName(t.lastMessage)}
                                    </span>
                                    <span className="mx-1 opacity-50" aria-hidden>
                                      ·
                                    </span>
                                    <time
                                      className="tabular-nums"
                                      dateTime={t.lastMessage.createdAt || undefined}
                                    >
                                      {formatDateTime(t.lastMessage.createdAt)}
                                    </time>
                                  </p>
                                  {(t.messageCount ?? 0) > 1 ? (
                                    <span className={messageCountChipClassName(theme)}
                                      title={`${t.messageCount} messages in thread`}
                                    >
                                      {t.messageCount}
                                    </span>
                                  ) : null}
                                </div>
                              </button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                        {isPayloadOpen ? (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              className="p-0 border-t-0"
                              id={`ticket-payload-${t.id}`}
                            >
                              <div
                                className={cn(
                                  'border-t px-4 py-4 sm:px-5',
                                  theme.border.light,
                                  ticketingExpandedPayloadBgClassName(theme),
                                )}
                              >
                                <div className="max-w-[66vw]">
                                  <Label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Request details
                                  </Label>
                                  <TicketingPayloadViewer payload={t.payload} formKey={t.formKey} />
                                  <TicketingOriginalDescriptionFooter
                                    payload={t.payload}
                                    formKey={t.formKey}
                                    className="mt-4"
                                  />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <TicketingConversationModal
        isOpen={conversationTicket != null}
        onClose={() => setConversationTicket(null)}
        ticket={conversationTicket}
      />

      <Modal isOpen={modalOpen} onClose={closeModal} size="xl">
        <ModalHeader onClose={closeModal}>
          <ModalTitle>Ticket #{selected?.id}</ModalTitle>
          <ModalDescription>
            {selected
              ? `${selected.ticketingCategory?.ticketingDepartment?.name || ''} · ${
                  selected.ticketingCategory?.name || ''
                }`
              : ''}
          </ModalDescription>
        </ModalHeader>
        <ModalContent className="space-y-4 max-h-[70vh] overflow-y-auto">
          {selected ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className={cn('font-medium', theme.text.muted)}>Ticket date: </span>
                  <span>{formatShortDate(selected.ticketDate)}</span>
                </div>
                <div>
                  <span className={cn('font-medium', theme.text.muted)}>Form: </span>
                  <code>{selected.formKey}</code>
                </div>
                <div>
                  <span className={cn('font-medium', theme.text.muted)}>Requested by: </span>
                  <span>
                    {selected.ticketRequestedFromUser?.userDisplayName ||
                      selected.ticketRequestedFromUser?.userEmail ||
                      '—'}
                  </span>
                </div>
                <div>
                  <span className={cn('font-medium', theme.text.muted)}>Created: </span>
                  <span>{formatDateTime(selected.createdAt)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="it-ticket-status" className="mb-1.5 block">
                    Status
                  </Label>
                  <Select
                    id="it-ticket-status"
                    name="editStatus"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    showClearButton={false}
                    className="w-full"
                  >
                    {EDIT_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <Label htmlFor="it-ticket-assignee" className="mb-1.5 block">
                    Assignee
                  </Label>
                  <Select
                    id="it-ticket-assignee"
                    name="editAssignedToUserId"
                    value={editAssignedToUserId}
                    onChange={(e) => setEditAssignedToUserId(e.target.value)}
                    showClearButton={false}
                    className="w-full"
                  >
                    <SelectItem value="">N/A</SelectItem>
                    {assignableUsers.map((u) => {
                      const label =
                        u.userDisplayName || u.userEmail || (u.id != null ? `User #${u.id}` : 'Unknown');
                      return (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </Select>
                </div>

                <div>
                  <Label htmlFor="it-ticket-due-at" className="mb-1.5 block">
                    Due date
                  </Label>
                  <DateInput
                    id="it-ticket-due-at"
                    name="editDueAt"
                    value={editDueAt}
                    onChange={(e) => setEditDueAt(e.target.value)}
                    showClearButton={false}
                    showFormatHint={false}
                    className="w-full"
                  />
                  <p className={cn('text-xs mt-1', theme.text.muted)}>
                    Leave empty to clear due date.
                  </p>
                </div>
              </div>

              <div className={ticketingInfoPanelClassName(theme)}>
                <div className="mb-1 flex items-center gap-1.5">
                  <InfoIcon className={cn('h-5 w-5', ticketingInfoPanelTitleClassName(theme))} />
                  <Label
                    className={cn(
                      'm-0 text-xs font-semibold uppercase tracking-wide mt-2',
                      ticketingInfoPanelTitleClassName(theme),
                    )}
                  >
                    Assignee transfer history
                  </Label>
                </div>
                {assigneeHistorySteps.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {assigneeHistorySteps.map((step, index) => (
                      <React.Fragment key={`${step.user}-${step.date}-${index}`}>
                        <span
                          className={cn(
                            'inline-flex items-center px-2.5 py-1 text-xs font-semibold',
                            assigneeHistoryStepBadgeClassName(theme),
                          )}
                        >
                          {step.user || 'N/A'}
                        </span>
                        {step.date ? (
                          <span className={cn('text-xs', theme.text.muted)}>{step.date}</span>
                        ) : null}
                        {index < assigneeHistorySteps.length - 1 ? (
                          <span className={cn('text-xs font-semibold', theme.text.muted)}>{'->'}</span>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <p className={cn('m-0 text-sm whitespace-pre-wrap break-words', theme.text.primary)}>
                    No transfer history yet.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="it-ticket-notes" className="mb-1.5 block">
                  Notes
                </Label>
                <Textarea
                  id="it-ticket-notes"
                  rows={3}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes..."
                  className="w-full"
                />
              </div>

              <div
                className={cn(
                  'rounded-lg border px-3 py-2 space-y-2',
                  theme.border.light,
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="m-0">Attachments</Label>
                  <div>
                    <input
                      ref={modalFileRef}
                      type="file"
                      className="hidden"
                      disabled={modalUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        await uploadAttachmentForSelected(file);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={modalUploading}
                      onClick={() => modalFileRef.current?.click()}
                    >
                      {modalUploading ? 'Uploading…' : 'Upload file'}
                    </Button>
                  </div>
                </div>
                <div
                  className={cn(
                    'rounded-lg border-2 border-dashed px-3 py-4 text-center text-sm transition-colors',
                    isDragOverAttachments
                      ? ticketingDropZoneActiveClassName()
                      : ticketingDropZoneIdleClassName(theme),
                    modalUploading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer',
                  )}
                  onDragOver={(e) => {
                    if (modalUploading) return;
                    e.preventDefault();
                    setIsDragOverAttachments(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragOverAttachments(false);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    setIsDragOverAttachments(false);
                    if (modalUploading) return;
                    const file = e.dataTransfer?.files?.[0];
                    await uploadAttachmentForSelected(file);
                  }}
                  onClick={() => {
                    if (!modalUploading) modalFileRef.current?.click();
                  }}
                  role="button"
                  tabIndex={modalUploading ? -1 : 0}
                  onKeyDown={(e) => {
                    if (modalUploading) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      modalFileRef.current?.click();
                    }
                  }}
                >
                  <p className={cn('m-0', theme.text.secondary)}>
                    Drag and drop a file here, or click to browse.
                  </p>
                </div>
                {mergedEditAttachments.length ? (
                  <ul className="list-none p-0 m-0 space-y-1 text-sm">
                    {mergedEditAttachments.map((a) => (
                      <li key={a.id} className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={cn('h-auto p-0', ticketingLinkClassName(theme))}
                          onClick={async () => {
                            let url;
                            try {
                              url = await downloadTicketingAttachmentObjectUrl(selected.id, a.id);
                              const dl = document.createElement('a');
                              dl.href = url;
                              dl.download = normalizeFileName(a.fileName || 'file');
                              document.body.appendChild(dl);
                              dl.click();
                              dl.remove();
                            } catch (err) {
                              alert(err?.message || 'Download failed');
                            } finally {
                              if (url) URL.revokeObjectURL(url);
                            }
                          }}
                        >
                          {normalizeFileName(a.fileName || '')}
                        </Button>
                        <span
                          className={cn(
                            'text-[10px] uppercase tracking-wide px-1.5 py-0.5',
                            attachmentSourceBadgeClassName(a.displaySource, theme),
                          )}
                        >
                          {a.displaySource === 'comments' ? 'Comments' : 'Ticket'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={cn('text-xs m-0', theme.text.muted)}>No attachments</p>
                )}
              </div>

              {selected.auditEntries?.length ? (
                <div>
                  <Label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
                    Recent activity
                  </Label>
                  <div
                    className={cn(
                      'max-h-40 overflow-y-auto rounded-lg border px-2 py-2 text-xs space-y-2',
                      theme.border.light,
                    )}
                  >
                    {selected.auditEntries.slice(0, 30).map((entry) => (
                      <div key={entry.id} className={cn('border-b last:border-0 pb-2', theme.border.light)}>
                        <span className="font-medium">{auditActionLabel(entry.action)}</span>
                        <span className={cn('text-[10px] ml-2', theme.text.muted)}>
                          {entry.actor?.userDisplayName || entry.actor?.userEmail || '—'} ·{' '}
                          {formatDateTime(entry.createdAt)}
                        </span>
                        {(entry.previousValue != null && entry.previousValue !== '') ||
                        (entry.newValue != null && entry.newValue !== '') ? (
                          <p className={cn('m-0 mt-0.5 whitespace-pre-wrap', theme.text.secondary)}>
                            {entry.previousValue ? `Was: ${entry.previousValue} ` : ''}
                            {entry.newValue ? `→ ${entry.newValue}` : ''}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div>
                <Label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
                  Request details
                </Label>
                <TicketingPayloadViewer payload={selected.payload} formKey={selected.formKey} />
              </div>

              <TicketingOriginalDescriptionFooter
                payload={selected.payload}
                formKey={selected.formKey}
              />
            </>
          ) : null}
        </ModalContent>
        <ModalFooter className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              if (selected) setConversationTicket(selected);
              closeModal();
            }}
            disabled={saving || !selected}
          >
            Open comments
          </Button>
          <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSave} disabled={saving || !selected}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={attachmentsModalOpen} onClose={closeAttachmentsModal} size="lg">
        <ModalHeader onClose={closeAttachmentsModal}>
          <ModalTitle>Attachments · Ticket #{attachmentsTicket?.id}</ModalTitle>
          <ModalDescription>
            Attachment links only
          </ModalDescription>
        </ModalHeader>
        <ModalContent className="space-y-3 max-h-[60vh] overflow-y-auto">
          {attachmentsLoading ? (
            <p className={cn('text-sm', theme.text.muted)}>Loading attachments…</p>
          ) : mergedAttachmentsModalList.length ? (
            <ul className="list-none p-0 m-0 space-y-2">
              {mergedAttachmentsModalList.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn('h-auto p-0 text-sm', ticketingLinkClassName(theme))}
                    onClick={async () => {
                      let url;
                      try {
                        url = await downloadTicketingAttachmentObjectUrl(
                          attachmentsTicket.id,
                          a.id,
                        );
                        const dl = document.createElement('a');
                        dl.href = url;
                        dl.download = normalizeFileName(a.fileName || 'file');
                        document.body.appendChild(dl);
                        dl.click();
                        dl.remove();
                      } catch (err) {
                        alert(err?.message || 'Download failed');
                      } finally {
                        if (url) URL.revokeObjectURL(url);
                      }
                    }}
                  >
                    {normalizeFileName(a.fileName || '')}
                  </Button>
                  <span
                    className={cn(
                      'text-[10px] uppercase tracking-wide px-1.5 py-0.5',
                      attachmentSourceBadgeClassName(a.displaySource, theme),
                    )}
                  >
                    {a.displaySource === 'comments' ? 'Comments' : 'Ticket'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={cn('text-sm', theme.text.muted)}>No attachments</p>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={closeAttachmentsModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteTicket}
        title="Delete ticket"
        message={
          ticketPendingDelete?.id != null
            ? `Are you sure you want to delete ticket #${ticketPendingDelete.id}?`
            : 'Are you sure you want to delete this ticket?'
        }
        details={
          ticketPendingDelete
            ? {
                Ticket: `#${ticketPendingDelete.id}`,
                ...(ticketSubjectText(ticketPendingDelete)
                  ? { Subject: ticketSubjectText(ticketPendingDelete) }
                  : {}),
              }
            : null
        }
        warningMessage="This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deleteSubmitting}
      />
    </div>
  );
};

export default TicketingItQueuePage;
